/**
 * @fileoverview Lightweight contact popover for a mail sender (MOR-529).
 *
 * Wraps a trigger (the sender name) and, on hover/click, resolves the address
 * against the job's contacts (reusing `listJobContacts` — the same query the
 * Contacts view runs) to show name, roles, email, and phone. Falls back to
 * just the address when no contact matches.
 */

import { useMemo } from "react";
import { useQuery } from "convex/react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { ROLE_META } from "@/lib/contact-roles";

interface ContactPopoverCardProps {
  jobId: Id<"jobs">;
  email: string;
  children: React.ReactNode;
}

export function ContactPopoverCard({
  jobId,
  email,
  children,
}: ContactPopoverCardProps) {
  const contacts = useQuery(api.contacts.queries.listJobContacts, { jobId });

  const contact = useMemo(() => {
    if (!contacts) return null;
    const target = email.trim().toLowerCase();
    if (!target) return null;
    return (
      contacts.find((c) => {
        const emails = [c.effectiveEmail, c.email].filter((e): e is string =>
          Boolean(e),
        );
        return emails.some((e) => e.toLowerCase() === target);
      }) ?? null
    );
  }, [contacts, email]);

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        {contact ? (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-foreground">
              {contact.displayName}
            </div>
            {contact.roles && contact.roles.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {contact.roles.map((role) => (
                  <Badge
                    key={role}
                    variant="soft"
                    color={ROLE_META[role].badgeColor}
                    className="text-[11px]"
                  >
                    {ROLE_META[role].label}
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-1 text-xs text-muted-foreground">
              {(contact.effectiveEmail ?? contact.email) && (
                <div>{contact.effectiveEmail ?? contact.email}</div>
              )}
              {(contact.effectivePhone ?? contact.phone) && (
                <div>{contact.effectivePhone ?? contact.phone}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="text-sm font-medium text-foreground">{email}</div>
            <p className="text-xs text-muted-foreground">
              Not linked to a contact on this job.
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
